/** Google Ad Manager slot tanımları — haber.com prod yapısına göre */

export type AdSlotFormat = 'horizontal' | 'square' | 'vertical' | 'pageskin' | 'pixel' | 'interstitial';

export type GptSize = [number, number] | 'fluid';

/** GPT sizeMapping satırı — min viewport genişliği */
export type GptSizeMapEntry = {
  minWidth: number;
  sizes: GptSize[];
};

export type AdSlotDefinition = {
  label: string;
  hint: string;
  format: AdSlotFormat;
  gamUnitKey: string;
  defaultGamUnit: string;
  enabledKey: string;
  /** Yenileme aralığı (sn) — 0 = kapalı */
  refreshSecKey: string;
  defaultRefreshSec: number;
  sizes: GptSize[];
  /**
   * Responsive sizeMapping (AdSense esnek banner benzeri).
   * Büyük viewport’tan küçüğe tanımlanır; GPT doğru boyutu seçer.
   */
  sizeMapping?: GptSizeMapEntry[];
  /** AdSense slot setting key (legacy) */
  adsenseKey: string;
};

/**
 * Masthead / yatay banner — MREC yok (300x250 küçük kalıyordu).
 * AdSense adslot_1: mobilde fluid/320, ≥728’te leaderboard.
 */
const MASTHEAD_SIZE_MAPPING: GptSizeMapEntry[] = [
  { minWidth: 1000, sizes: ['fluid', [970, 250], [940, 250], [728, 90]] },
  { minWidth: 800, sizes: ['fluid', [728, 90], [970, 90]] },
  { minWidth: 500, sizes: ['fluid', [728, 90], [320, 100]] },
  { minWidth: 0, sizes: ['fluid', [320, 100], [320, 50]] },
];

const MASTHEAD_SIZES: GptSize[] = [
  'fluid',
  [320, 50],
  [320, 100],
  [728, 90],
  [970, 90],
  [970, 250],
  [940, 250],
];

/** İçerik / orta / alt — kutu %100, fluid + MREC / leaderboard */
const INCONTENT_SIZE_MAPPING: GptSizeMapEntry[] = [
  { minWidth: 800, sizes: ['fluid', [728, 90], [336, 280], [300, 250]] },
  { minWidth: 500, sizes: ['fluid', [336, 280], [300, 250], [320, 100]] },
  { minWidth: 0, sizes: ['fluid', [300, 250], [336, 280], [320, 100], [320, 50]] },
];

const INCONTENT_SIZES: GptSize[] = [
  'fluid',
  [320, 50],
  [320, 100],
  [300, 250],
  [336, 280],
  [728, 90],
];

/** Sidebar — kolon genişliğinde fluid */
const SIDEBAR_SIZE_MAPPING: GptSizeMapEntry[] = [
  { minWidth: 0, sizes: ['fluid', [300, 250], [300, 600]] },
];

const SIDEBAR_SIZES: GptSize[] = ['fluid', [300, 250], [300, 600]];

function slotRefreshKey(slotId: string): string {
  return `ads_slot_${slotId.replace(/-/g, '_')}_refresh_sec`;
}

export const GAM_NETWORK_PATH_DEFAULT = '/23277273038';

export const AD_SLOT_DEFINITIONS: Record<string, AdSlotDefinition> = {
  'home-top': {
    label: 'Ana sayfa masthead',
    hint: 'Manşet üstü — Masthead_ROS',
    format: 'horizontal',
    gamUnitKey: 'gam_unit_masthead',
    defaultGamUnit: 'Masthead_ROS',
    enabledKey: 'ads_slot_home_top_enabled',
    refreshSecKey: slotRefreshKey('home-top'),
    defaultRefreshSec: 0,
    sizes: MASTHEAD_SIZES,
    sizeMapping: MASTHEAD_SIZE_MAPPING,
    adsenseKey: 'adsense_slot_home_top',
  },
  'category-top': {
    label: 'Kategori masthead',
    hint: 'Kategori sayfası üst — Masthead_ROS',
    format: 'horizontal',
    gamUnitKey: 'gam_unit_masthead',
    defaultGamUnit: 'Masthead_ROS',
    enabledKey: 'ads_slot_category_top_enabled',
    refreshSecKey: slotRefreshKey('category-top'),
    defaultRefreshSec: 0,
    sizes: MASTHEAD_SIZES,
    sizeMapping: MASTHEAD_SIZE_MAPPING,
    adsenseKey: 'adsense_slot_home_top',
  },
  'article-banner': {
    label: 'Haber sayfa banner',
    hint: 'İlk haber üstü — Masthead_ROS (haber.com)',
    format: 'horizontal',
    gamUnitKey: 'gam_unit_masthead',
    defaultGamUnit: 'Masthead_ROS',
    enabledKey: 'ads_slot_article_banner_enabled',
    refreshSecKey: slotRefreshKey('article-banner'),
    defaultRefreshSec: 0,
    sizes: MASTHEAD_SIZES,
    sizeMapping: MASTHEAD_SIZE_MAPPING,
    adsenseKey: '',
  },
  'article-top': {
    label: 'Haber üst',
    hint: 'Başlık altı — Masthead_Top / Masthead_ROS',
    format: 'horizontal',
    gamUnitKey: 'gam_unit_masthead_top',
    defaultGamUnit: 'Masthead_Top',
    enabledKey: 'ads_slot_article_top_enabled',
    refreshSecKey: slotRefreshKey('article-top'),
    defaultRefreshSec: 0,
    sizes: MASTHEAD_SIZES,
    sizeMapping: MASTHEAD_SIZE_MAPPING,
    adsenseKey: 'adsense_slot_article_top',
  },
  'home-middle': {
    label: 'Ana sayfa orta',
    hint: 'Esnek içerik — fluid / 300x250 / 728',
    format: 'square',
    gamUnitKey: 'gam_unit_web_300x250',
    defaultGamUnit: 'Web_300x250_Ros',
    enabledKey: 'ads_slot_home_middle_enabled',
    refreshSecKey: slotRefreshKey('home-middle'),
    defaultRefreshSec: 60,
    sizes: INCONTENT_SIZES,
    sizeMapping: INCONTENT_SIZE_MAPPING,
    adsenseKey: 'adsense_slot_home_middle',
  },
  'article-mid': {
    label: 'Haber orta',
    hint: 'İçerik ortası — esnek %100',
    format: 'square',
    gamUnitKey: 'gam_unit_web_300x250',
    defaultGamUnit: 'Web_300x250_Ros',
    enabledKey: 'ads_slot_article_mid_enabled',
    refreshSecKey: slotRefreshKey('article-mid'),
    defaultRefreshSec: 60,
    sizes: INCONTENT_SIZES,
    sizeMapping: INCONTENT_SIZE_MAPPING,
    adsenseKey: 'adsense_slot_article_mid',
  },
  'article-bottom': {
    label: 'Haber alt',
    hint: 'İçerik altı — esnek %100',
    format: 'square',
    gamUnitKey: 'gam_unit_web_300x250',
    defaultGamUnit: 'Web_300x250_Ros',
    enabledKey: 'ads_slot_article_bottom_enabled',
    refreshSecKey: slotRefreshKey('article-bottom'),
    defaultRefreshSec: 0,
    sizes: INCONTENT_SIZES,
    sizeMapping: INCONTENT_SIZE_MAPPING,
    adsenseKey: 'adsense_slot_article_bottom',
  },
  'home-bottom': {
    label: 'Ana sayfa alt',
    hint: 'Sayfa altı — esnek %100',
    format: 'horizontal',
    gamUnitKey: 'gam_unit_web_300x250',
    defaultGamUnit: 'Web_300x250_Ros',
    enabledKey: 'ads_slot_home_bottom_enabled',
    refreshSecKey: slotRefreshKey('home-bottom'),
    defaultRefreshSec: 0,
    sizes: INCONTENT_SIZES,
    sizeMapping: INCONTENT_SIZE_MAPPING,
    adsenseKey: 'adsense_slot_home_bottom',
  },
  sidebar: {
    label: 'Sidebar',
    hint: 'Yan panel — kolon %100 / fluid',
    format: 'vertical',
    gamUnitKey: 'gam_unit_web_300x250',
    defaultGamUnit: 'Web_300x250_Ros',
    enabledKey: 'ads_slot_sidebar_enabled',
    refreshSecKey: slotRefreshKey('sidebar'),
    defaultRefreshSec: 60,
    sizes: SIDEBAR_SIZES,
    sizeMapping: SIDEBAR_SIZE_MAPPING,
    adsenseKey: 'adsense_slot_sidebar',
  },
  'sidebar-tall': {
    label: 'Sidebar (300x600)',
    hint: 'Ana sayfa yan — kolon %100',
    format: 'vertical',
    gamUnitKey: 'gam_unit_web_300x600',
    defaultGamUnit: '300x600_Web',
    enabledKey: 'ads_slot_sidebar_tall_enabled',
    refreshSecKey: slotRefreshKey('sidebar-tall'),
    defaultRefreshSec: 60,
    sizes: ['fluid', [300, 600]],
    sizeMapping: [{ minWidth: 0, sizes: ['fluid', [300, 600]] }],
    adsenseKey: 'adsense_slot_sidebar',
  },
  'skyscraper-left': {
    label: 'Sol skyscraper',
    hint: '160x600 sol — site kolonuna bitişik',
    format: 'vertical',
    gamUnitKey: 'gam_unit_skyscraper_left',
    defaultGamUnit: '160x600_Sol_Ros',
    enabledKey: 'ads_slot_skyscraper_left_enabled',
    refreshSecKey: slotRefreshKey('skyscraper-left'),
    defaultRefreshSec: 0,
    sizes: [[160, 600]],
    adsenseKey: '',
  },
  'skyscraper-right': {
    label: 'Sağ skyscraper',
    hint: '160x600 sağ — site kolonuna bitişik',
    format: 'vertical',
    gamUnitKey: 'gam_unit_skyscraper_right',
    defaultGamUnit: '160x600_Sag_Ros',
    enabledKey: 'ads_slot_skyscraper_right_enabled',
    refreshSecKey: slotRefreshKey('skyscraper-right'),
    defaultRefreshSec: 0,
    sizes: [[160, 600]],
    adsenseKey: '',
  },
  'skyscraper-finance-left': {
    label: 'Ekonomi sol skyscraper',
    hint: 'Dunya_Finans_160x600_Sol — /ekonomi',
    format: 'vertical',
    gamUnitKey: 'gam_unit_skyscraper_finance_left',
    defaultGamUnit: 'Dunya_Finans_160x600_Sol',
    enabledKey: 'ads_slot_skyscraper_finance_left_enabled',
    refreshSecKey: slotRefreshKey('skyscraper-finance-left'),
    defaultRefreshSec: 0,
    sizes: [[160, 600]],
    adsenseKey: '',
  },
  'skyscraper-finance-right': {
    label: 'Ekonomi sağ skyscraper',
    hint: 'Dunya_Finans_160x600_Sag — /ekonomi',
    format: 'vertical',
    gamUnitKey: 'gam_unit_skyscraper_finance_right',
    defaultGamUnit: 'Dunya_Finans_160x600_Sag',
    enabledKey: 'ads_slot_skyscraper_finance_right_enabled',
    refreshSecKey: slotRefreshKey('skyscraper-finance-right'),
    defaultRefreshSec: 0,
    sizes: [[160, 600]],
    adsenseKey: '',
  },
  'pageskin-left': {
    label: 'Pageskin sol',
    hint: '490x1080 sol',
    format: 'pageskin',
    gamUnitKey: 'gam_unit_pageskin_left',
    defaultGamUnit: 'Pageskin_Sol',
    enabledKey: 'ads_slot_pageskin_left_enabled',
    refreshSecKey: slotRefreshKey('pageskin-left'),
    defaultRefreshSec: 60,
    sizes: [[490, 1080]],
    adsenseKey: '',
  },
  'pageskin-right': {
    label: 'Pageskin sağ',
    hint: '490x1080 sağ',
    format: 'pageskin',
    gamUnitKey: 'gam_unit_pageskin_right',
    defaultGamUnit: 'Pageskin_Sag',
    enabledKey: 'ads_slot_pageskin_right_enabled',
    refreshSecKey: slotRefreshKey('pageskin-right'),
    defaultRefreshSec: 60,
    sizes: [[490, 1080]],
    adsenseKey: '',
  },
  interstitial: {
    label: 'Interstitial',
    hint: 'Tam ekran arası reklam',
    format: 'interstitial',
    gamUnitKey: 'gam_unit_interstitial',
    defaultGamUnit: 'interstitial_oop',
    enabledKey: 'ads_slot_interstitial_enabled',
    refreshSecKey: slotRefreshKey('interstitial'),
    defaultRefreshSec: 0,
    sizes: [[1, 1], [320, 480], [800, 600]],
    adsenseKey: '',
  },
  pixel: {
    label: '1x1 piksel',
    hint: 'Görünmez impression',
    format: 'pixel',
    gamUnitKey: 'gam_unit_pixel',
    defaultGamUnit: '1x1_Imp',
    enabledKey: 'ads_slot_pixel_enabled',
    refreshSecKey: slotRefreshKey('pixel'),
    defaultRefreshSec: 0,
    sizes: [[1, 1]],
    adsenseKey: '',
  },
};

export const ADMIN_GAM_UNIT_KEYS = [
  'gam_unit_masthead',
  'gam_unit_masthead_top',
  'gam_unit_web_300x250',
  'gam_unit_web_300x600',
  'gam_unit_skyscraper_left',
  'gam_unit_skyscraper_right',
  'gam_unit_skyscraper_finance_left',
  'gam_unit_skyscraper_finance_right',
  'gam_unit_pageskin_left',
  'gam_unit_pageskin_right',
  'gam_unit_interstitial',
  'gam_unit_pixel',
] as const;

export const ADMIN_GAM_UNIT_LABELS: Record<(typeof ADMIN_GAM_UNIT_KEYS)[number], string> = {
  gam_unit_masthead: 'Masthead ROS (728/970)',
  gam_unit_masthead_top: 'Masthead Top (970x250)',
  gam_unit_web_300x250: 'Web 300x250 ROS',
  gam_unit_web_300x600: 'Web 300x600',
  gam_unit_skyscraper_left: '160x600 Sol',
  gam_unit_skyscraper_right: '160x600 Sağ',
  gam_unit_skyscraper_finance_left: 'Dünya Finans 160x600 Sol',
  gam_unit_skyscraper_finance_right: 'Dünya Finans 160x600 Sağ',
  gam_unit_pageskin_left: 'Pageskin Sol',
  gam_unit_pageskin_right: 'Pageskin Sağ',
  gam_unit_interstitial: 'Interstitial OOP',
  gam_unit_pixel: '1x1 Imp',
};

export const ADMIN_GAM_UNIT_DEFAULTS: Record<(typeof ADMIN_GAM_UNIT_KEYS)[number], string> = {
  gam_unit_masthead: 'Masthead_ROS',
  gam_unit_masthead_top: 'Masthead_Top',
  gam_unit_web_300x250: 'Web_300x250_Ros',
  gam_unit_web_300x600: '300x600_Web',
  gam_unit_skyscraper_left: '160x600_Sol_Ros',
  gam_unit_skyscraper_right: '160x600_Sag_Ros',
  gam_unit_skyscraper_finance_left: 'Dunya_Finans_160x600_Sol',
  gam_unit_skyscraper_finance_right: 'Dunya_Finans_160x600_Sag',
  gam_unit_pageskin_left: 'Pageskin_Sol',
  gam_unit_pageskin_right: 'Pageskin_Sag',
  gam_unit_interstitial: 'interstitial_oop',
  gam_unit_pixel: '1x1_Imp',
};

export function getAdSlotDefinition(slot: string): AdSlotDefinition | null {
  return AD_SLOT_DEFINITIONS[slot] ?? null;
}

/**
 * Kolon genişliğine göre boyut — yalnızca sığan size’lar (iframe scale yok).
 * Wrapper div width:100%; creative doğal boyutta kalır.
 */
export function resolveSizesForContainerWidth(
  def: AdSlotDefinition,
  widthPx: number,
): { sizes: GptSize[]; sizeMapping: undefined } {
  const w = Math.max(0, Math.floor(widthPx));

  if (def.format === 'horizontal') {
    if (w >= 1000) {
      return { sizes: ['fluid', [970, 250], [940, 250], [728, 90]], sizeMapping: undefined };
    }
    if (w >= 728) {
      return { sizes: ['fluid', [728, 90], [970, 90]], sizeMapping: undefined };
    }
    if (w >= 500) {
      return { sizes: ['fluid', [320, 100], [320, 50]], sizeMapping: undefined };
    }
    return { sizes: ['fluid', [320, 100], [320, 50]], sizeMapping: undefined };
  }

  if (def.format === 'vertical') {
    return { sizes: ['fluid', [300, 250], [300, 600]], sizeMapping: undefined };
  }

  // square / in-content
  if (w >= 728) {
    return { sizes: ['fluid', [728, 90], [336, 280], [300, 250]], sizeMapping: undefined };
  }
  if (w >= 336) {
    return { sizes: ['fluid', [336, 280], [300, 250]], sizeMapping: undefined };
  }
  return { sizes: ['fluid', [300, 250], [320, 100]], sizeMapping: undefined };
}

/** Slot DOM’da gerçekten görünür creative var mı? */
export function isAdDomFilled(root: HTMLElement | null, minPx = 24): boolean {
  if (!root) return false;
  const iframe = root.querySelector('iframe') as HTMLIFrameElement | null;
  if (iframe) {
    const h = iframe.offsetHeight || iframe.clientHeight || 0;
    const w = iframe.offsetWidth || iframe.clientWidth || 0;
    if (h >= minPx && w >= minPx) return true;
  }
  const ins = root.querySelector('ins.adsbygoogle') as HTMLElement | null;
  if (ins) {
    const h = ins.offsetHeight || ins.getBoundingClientRect().height;
    const w = ins.offsetWidth || ins.getBoundingClientRect().width;
    if (h >= minPx && w >= minPx) return true;
  }
  const h = root.offsetHeight || root.getBoundingClientRect().height;
  const w = root.offsetWidth || root.getBoundingClientRect().width;
  return h >= minPx && w >= minPx && Boolean(iframe || ins);
}
